// src/features/screener/ScreenerPage.tsx
//
// The Global Screener feature page. A live, institutional-terminal style
// multi-dimensional asset screener backed by the in-memory Polars engine
// (POST /api/v1/global-search/screener/search). Reuses the standard
// <Layout sidebar={<Sidebar />}> dashboard shell so it sits behind the
// same auth gate / navigation as the rest of the suite.
//
// Live-update model:
//   * <ScreenerFilters /> owns the raw inputs and emits a normalised
//     filter object upward on every change.
//   * That object is debounced (350ms) here, so a burst of keystrokes
//     collapses into a single backend call once the user pauses.
//   * A monotonically-increasing request id guarantees the LAST issued
//     query always wins, even if an earlier (slower) response lands late.
//
// Selecting a row routes to the matching asset deep-dive page, keyed off
// the asset_class discriminant (identical contract to <GlobalSearchBar/>).

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { GlobalSearchBar } from '../global-search';
import { Layout, Sidebar } from '../../shared/ui';
import { useDebounce } from '../../shared/hooks/useDebounce';
import {
    fetchScreenerOptions,
    runScreener,
    type AssetClass,
    type ScreenerFilters,
    type ScreenerOptions,
    type ScreenerResult,
    type ScreenerSortKey,
} from './api';
import { ScreenerFiltersPanel } from './components/ScreenerFilters';
import { ScreenerResultsTable } from './components/ScreenerResultsTable';

const RESULT_LIMIT = 100;

// Deep-dive route per asset class — mirrors <GlobalSearchBar />.
const ROUTE_BY_CLASS: Record<AssetClass, string> = {
    equity: '/dashboard/stock-info-page',
    etf: '/dashboard/etf-info-page',
    reit: '/dashboard/reit-info-page',
    crypto: '/dashboard/crypto-info-page',
};

export const ScreenerPage = () => {
    const navigate = useNavigate();

    const [options, setOptions] = useState<ScreenerOptions>({
        sectors: [],
        exchanges: [],
    });
    // Seed with the panel's default (NYSE equities) so the very first query
    // is already exchange-scoped — market caps are stored in each listing's
    // native currency, so an unfiltered market-cap sort would surface e.g.
    // KRW-denominated names above USD ones on open.
    const [filters, setFilters] = useState<ScreenerFilters>({
        exchange: 'NYSE',
        is_etf: false,
    });
    // Server-side sort — defaults to biggest companies first.
    const [sortBy, setSortBy] = useState<ScreenerSortKey>('market_cap');
    const [sortDesc, setSortDesc] = useState(true);
    const [results, setResults] = useState<ScreenerResult[]>([]);
    const [totalMatches, setTotalMatches] = useState(0);
    const [execMs, setExecMs] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Debounce the filter payload so rapid input collapses into one call.
    const debouncedFilters = useDebounce(filters, 350);

    // True when the debounced request is strictly targeting CRYPTO. Tying this
    // to debouncedFilters means the columns disappear at the exact moment the
    // table dims to fetch crypto data — no premature layout flicker.
    const isCryptoMode = debouncedFilters.exchange === 'CRYPTO';

    // Safeguard: if the user switches to CRYPTO while sorting by a column that
    // is about to disappear (beta / dividend), reset the sort to Market Cap so
    // the table never sorts by a ghost column.
    useEffect(() => {
        if (isCryptoMode && (sortBy === 'beta' || sortBy === 'dividend')) {
            setSortBy('market_cap');
            setSortDesc(true);
        }
    }, [isCryptoMode, sortBy]);

    // Monotonic guard so a stale (late) response can never overwrite a
    // newer one issued after it.
    const requestSeq = useRef(0);

    // --- Load dropdown options once on mount. ---------------------------
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const opts = await fetchScreenerOptions();
            if (!cancelled) setOptions(opts);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // --- Run the screener whenever the debounced filters change. --------
    useEffect(() => {
        // Each run owns an AbortController so the cleanup below can physically
        // sever the in-flight HTTP request the moment a newer keystroke/sort
        // supersedes it — FastAPI then stops evaluating the abandoned Polars
        // search instead of wasting CPU on a result nobody will read.
        const abortController = new AbortController();
        const seq = ++requestSeq.current;
        // Keep the first mount as a full-page spinner; subsequent live
        // updates use the lighter "refreshing" dim so the table doesn't
        // flash away under the user.
        setIsRefreshing(true);

        (async () => {
            try {
                const res = await runScreener(
                    debouncedFilters,
                    RESULT_LIMIT,
                    { sortBy, sortDesc },
                    abortController.signal,
                );
                if (seq !== requestSeq.current) return; // superseded
                setResults(res.results);
                setTotalMatches(res.total_matches);
                setExecMs(res.execution_time_ms);
                setError(null);
            } catch (err: unknown) {
                // A cancelled request is expected teardown, not a failure.
                const e = err as { name?: string; code?: string };
                if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') {
                    return;
                }
                if (seq !== requestSeq.current) return;
                // eslint-disable-next-line no-console
                console.error('[screener] search failed', err);
                setError(
                    'Unable to run the screener right now. Please try again shortly.',
                );
            } finally {
                // Only clear the loading state if this run is still the
                // current one AND it wasn't aborted (an abort means a newer
                // run is already in flight and owns the spinner).
                if (seq === requestSeq.current && !abortController.signal.aborted) {
                    setIsLoading(false);
                    setIsRefreshing(false);
                }
            }
        })();

        // Cleanup cancels the previous network request immediately.
        return () => abortController.abort();
        // Sort changes fire immediately (not debounced) — a header click
        // should feel instant, while filter typing stays debounced.
    }, [debouncedFilters, sortBy, sortDesc]);

    const handleRowClick = (row: ScreenerResult) => {
        const path = ROUTE_BY_CLASS[row.asset_class] ?? ROUTE_BY_CLASS.equity;
        navigate(`${path}?symbol=${encodeURIComponent(row.symbol)}`);
    };

    // Header click: toggle direction when re-selecting the active column,
    // otherwise switch column and default to descending (biggest first).
    const handleSortChange = (key: ScreenerSortKey) => {
        if (key === sortBy) {
            setSortDesc((d) => !d);
        } else {
            setSortBy(key);
            setSortDesc(true);
        }
    };

    // "Showing X of Y" — Y is the true pre-limit match count from the
    // engine; X is what actually landed in the table (capped at the limit).
    const showingCapped = totalMatches > results.length;

    return (
        <Layout
            sidebar={<Sidebar />}
            title="Global Screener"
            subtitle="Filter the entire tradable universe across eight dimensions — in milliseconds."
        >
            {/* Platform-wide asset search — jump straight to any deep-dive. */}
            <div className="mb-8">
                <GlobalSearchBar />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6 items-start">
                {/* Filter rail — sticky on desktop so it stays in view while
                    the results scroll. */}
                <div className="lg:sticky lg:top-6">
                    <ScreenerFiltersPanel options={options} onChange={setFilters} />
                </div>

                {/* Results column. */}
                <div className="min-w-0">
                    {/* Stats bar: live match count + engine latency. */}
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-black tracking-tight text-slate-900 dark:text-white tabular-nums">
                                {totalMatches.toLocaleString()}
                            </span>
                            <span className="muted text-sm">
                                {totalMatches === 1 ? 'match' : 'matches'}
                                {showingCapped && (
                                    <> · showing top {results.length}</>
                                )}
                            </span>
                        </div>
                        {execMs != null && (
                            <span className="inline-flex items-center gap-1.5 text-xs font-mono text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-lg">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                {execMs.toFixed(2)} ms
                            </span>
                        )}
                    </div>

                    {error && (
                        <div className="mb-4 surface-card-error text-sm text-red-700 dark:text-red-400">
                            {error}
                        </div>
                    )}

                    {isLoading ? (
                        <div className="surface-card h-64 flex items-center justify-center muted">
                            Loading the universe…
                        </div>
                    ) : results.length === 0 && !error ? (
                        <div className="surface-card h-64 flex flex-col items-center justify-center text-center gap-2">
                            <p className="font-semibold text-slate-700 dark:text-slate-200">
                                No assets match these filters.
                            </p>
                            <p className="muted text-sm">
                                Try loosening a constraint — e.g. a lower market
                                cap or a wider price range.
                            </p>
                        </div>
                    ) : (
                        <ScreenerResultsTable
                            results={results}
                            onRowClick={handleRowClick}
                            sortBy={sortBy}
                            sortDesc={sortDesc}
                            onSortChange={handleSortChange}
                            isRefreshing={isRefreshing}
                            isCryptoMode={isCryptoMode}
                        />
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default ScreenerPage;
